/**
 * Joining a business's partner account from an invitation link: see whose
 * team it is, set a password, and you're in.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Card, ErrorNote, Field, Spinner } from '../components/Primitives';

export function InvitePage() {
  const T = useT();
  const { token = '' } = useParams();
  const nav = useNavigate();
  const { signIn } = useSession();
  const [invite, setInvite] = useState<{
    name: string;
    email: string;
    business: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<{ name: string; email: string; business: string }>(
        `/v1/partner/invites/${encodeURIComponent(token)}`,
      )
      .then(setInvite, (e: unknown) =>
        setError(
          e instanceof ApiError && e.status === 404
            ? T(
                'यह न्योता पुराना हो गया या पहले ही इस्तेमाल हो चुका है। जिसने भेजा, उनसे नया लिंक माँगें।',
                'This invitation has expired or was already used. Ask whoever sent it for a new link.',
              )
            : e instanceof ApiError
              ? e.message
              : 'Error',
        ),
      );
  }, [token]);
  if (error)
    return (
      <div className="mx-auto max-w-md p-4">
        <ErrorNote message={error} />
      </div>
    );
  if (!invite) return <Spinner />;
  const accept = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ token: string }>(
        `/v1/partner/invites/${encodeURIComponent(token)}/accept`,
        { password },
      );
      await signIn(r.token);
      nav('/', { replace: true });
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-md space-y-4 p-4" data-sec="jobs">
      <header
        className="relative overflow-hidden rounded-[26px] p-5 text-white shadow-lift"
        style={{
          backgroundImage: 'linear-gradient(140deg, #0f766e 0%, #6d28d9 60%, #db2777 100%)',
        }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/15"
        />
        <img
          src={`${import.meta.env.BASE_URL}lulu-logo.svg`}
          alt=""
          className="relative h-10 w-10 rounded-xl ring-2 ring-white/40"
        />
        <h1 className="relative mt-4 font-display text-[26px] font-bold leading-tight">
          {T('टीम में आपका स्वागत है', 'Welcome to the team')}
        </h1>
      </header>
      <Card>
        <p>
          {T(
            `${invite.business} ने आपको लुलु पार्टनर पर जोड़ा है।`,
            `${invite.business} added you on Lulu Partner.`,
          )}
        </p>
        <p className="mt-1 text-sm text-muted">
          {invite.name} · {invite.email}
        </p>
        <div className="mt-4 space-y-3">
          <Field
            label={T('पासवर्ड बनाएँ', 'Choose a password')}
            hint={T('कम से कम 8 अक्षर', 'At least 8 characters')}
          >
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <button
            className="btn-primary"
            disabled={busy || password.length < 8}
            onClick={() => void accept()}
          >
            {T('जुड़ें', 'Join')}
          </button>
          {msg && <p className="text-sm text-offline">{msg}</p>}
        </div>
      </Card>
    </div>
  );
}
